/**
 * Contact Autocomplete Hook
 * 
 * Provides autocomplete functionality for contact search using Elasticsearch.
 * Supports debounced search with minimum 2 characters and popular items on empty query.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { v1Api } from '../../../api/generatedClient'
import { getApiBaseUrl } from '../../../api/authTokens'
import type { APIContactAutocompleteResponse } from '../../../generated-types/models/APIContactAutocompleteResponse'
import type { V1SearchContactsAutocompleteRetrieveRequest } from '../../../generated-types/apis/V1Api'

interface ContactAutocompleteResponse {
  suggestions: Array<{
    id: string
    name: string
    email: string
    phone?: string
    mobile?: string
    self: string
  }>
  total: number
}

/**
 * Fetches contact autocomplete suggestions
 */
const fetchContactAutocomplete = async (
  query: string,
  size = 10
): Promise<ContactAutocompleteResponse> => {
  const response: APIContactAutocompleteResponse = await v1Api.v1SearchContactsAutocompleteRetrieve({
    q: query || undefined,
    size,
  } as V1SearchContactsAutocompleteRetrieveRequest)

  const baseUrl = getApiBaseUrl()

  return {
    suggestions: response.suggestions.map(suggestion => ({
      id: suggestion.id,
      name: suggestion.fullName ?? suggestion.email ?? '',
      email: suggestion.email,
      phone: suggestion.phone,
      mobile: suggestion.mobile,
      self: `${baseUrl}v1/contacts/${suggestion.id}/`,
    })),
    total: response.total,
  }
}

/**
 * Hook for contact autocomplete
 * 
 * @param options - Configuration options
 * @returns Autocomplete hook result with suggestions and loading state
 */
export function useContactAutocomplete(
  options: {
    size?: number
    enabled?: boolean
  } = {}
) {
  const { size = 10, enabled = true } = options
  const [query, setQuery] = useState('')
  
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isLoading, error } = useQuery<ContactAutocompleteResponse, Error>({
    queryKey: ['contact-autocomplete', debouncedQuery],
    queryFn: () => fetchContactAutocomplete(debouncedQuery, size),
    enabled: enabled && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    staleTime: 30000,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    suggestions: data?.suggestions ?? [],
    total: data?.total ?? 0,
    isLoading: isLoading && (debouncedQuery.length >= 2 || debouncedQuery === ''),
    error,
    updateQuery: setQuery,
    query,
  }
}


